import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    Flex,
    Stack
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { FaEdit, FaPaperPlane, FaPrint, FaTrashAlt } from "react-icons/fa";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import SearchTable2 from "components/Tables/SearchTable2";
import Loading from "components/Loading/Loading";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { ROUTES } from "constants/routes-path";
import { getEmptyPageResponse, toTablePageableData } from "utils/utils";
import { permitteesService } from "services/permitteesService";
import { documentsPermitteesService } from "services/documentsPermitteesService";
import { PermitteesDocumentsFilter } from "./PermitteesDocumentsFilter";
import PermitteesDocumentsListPageCardHeader from "./PermitteesDocumentsListPageCardHeader";
import { AuthContext } from "../../../../contexts/AuthContext";
import { PERMITTEES_DOCUMENTS_LIST_URL_DEFAULTS } from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";

const PermitteesDocumentsList = () => {
    const { t } = useTranslation();
    const { searchParams, setSearchParams, hasActiveFilters, urlSearchParams } = useListUrlSync(
        PERMITTEES_DOCUMENTS_LIST_URL_DEFAULTS,
        { preserveParentId: true }
    );
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const dialogRef = useRef(undefined);
    const [response, setResponse] = useState(undefined);
    const [isLoadingDelete, setIsLoadingDelete] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const parentId = urlSearchParams.get("parent_id");
    const { getUserData } = useContext(AuthContext);
    const user = getUserData();

    const [permitteeData, setPermitteeData] = useState(undefined);
    const [isLoadingPermitteeData, setIsLoadingPermitteeData] = useState(false);

    const columns = useMemo(() => ([
        {
            Header: t("permittees.documents.list.table.header.type"),
            accessor: "type_description",
            id: "tipo"
        },
        {
            Header: t("permittees.documents.list.table.header.shipment"),
            accessor: "id"
        },
        {
            Header: t("permittees.documents.list.table.header.value"),
            accessor: "valor"
        },
        {
            Header: t("permittees.documents.list.table.header.date"),
            accessor: "data_criacao"
        },
        {
            Header: t("permittees.documents.list.table.header.expiration-date"),
            accessor: "data_validade"
        },
        {
            Header: t("permittees.documents.list.table.header.situation"),
            accessor: "situacao",
            disableSortBy: true,
        },
        {
            Header: t("permittees.documents.list.table.header.operations"),
            disableSortBy: true,
            actions: [
                {
                    label: t("system.actions.edit"),
                    onClick: (row) =>
                        navigate(`${ROUTES.permitteesDocumentsEditPath}/${row.original.id}?parent_id=${parentId}`, {
                            state: {
                                id: row.original.id,
                                parentId,
                            }
                        }),
                    icon: <FaEdit />
                },
                {
                    label: t("system.actions.remove"),
                    onClick: (row) => handleDeleteClick(row.original.id),
                    icon: <FaTrashAlt color="#E53E3E" />,
                },
                {
                    label: t('system.actions.print'),
                    onClick: (row) => handlePrintDocument(row.original),
                    visible: (row) => row.printable === true,
                    icon: <FaPrint />
                },
                {
                    label: t("permittees.documents.list.table.actions.send-detran"),
                    onClick: (row) =>
                        navigate(`${ROUTES.permitteedDocumentsSendDetranPath}/${row.original.id}?parent_id=${parentId}`, {
                            state: {
                                id: row.original.id,
                                parentId,
                            }
                        }),
                    visible: (row) => row.detran_acceptable === true,
                    icon: <FaPaperPlane />
                },
            ]
        },
    ]), [t, navigate, parentId]);

    const handleDeleteClick = (itemId) => {
        setSelectedItem(itemId);
        dialogRef.current?.displayDialog({
            title: t("documents.list.actions.remove"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const handleConfirmDelete = async () => {
        if (selectedItem) {
            try {
                setIsLoadingDelete(true);
                await documentsPermitteesService.delete(selectedItem);
                showToast({ title: t('documents.messages.success.delete'), status: "success" });
                fetchData();
            } catch (error) {
                showToast({ title: t(error), status: "error" });
            } finally {
                setIsLoadingDelete(false);
            }
        }
    };

    const handlePrintDocument = async (code) => {
        window.open(
            `${window.location.origin}${ROUTES.permitteePrintDocument.replace(":code", code.codigo_validacao).replace(":id", user?.id)}`,
            '_blank'
        );
    }

    const fetchData = async (params = {}) => {
        params.orderBy = params.orderBy ?? searchParams.orderBy ?? "data_criacao";
        params.orderDirection = params.orderDirection ?? searchParams.orderDirection ?? "desc";
        params.page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;

        if (response) setResponse(undefined);

        try {
            params = { ...searchParams, ...params };
            setSearchParams(params);
            const data = await documentsPermitteesService.listWithFilters(parentId, params);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        }
    };

    const clearPermitteesDocumentsFilters = () => {
        fetchData({
            page: 1,
            filters: { data_inicial: null, data_final: null },
        });
    };

    const fetchPermitteeData = async () => {
        if (!parentId) return;

        try {
            setIsLoadingPermitteeData(true);
            const data = await permitteesService.detail(parentId);
            setPermitteeData(data);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        } finally {
            setIsLoadingPermitteeData(false);
        }
    };

    useEffect(() => {
        fetchPermitteeData();
        fetchData();
    }, [parentId]);

    return (
        <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" gap="4" direction={{ sm: "column", md: "row" }}>
                <Button
                    w="160px"
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    alignSelf={{ sm: "flex-start", lg: null }}
                    onClick={() => {
                        navigate(`${ROUTES.permitteesDocumentsAddPath}?parent_id=${parentId}`, {
                            state: { parentId }
                        });
                    }}
                >
                    {t("documents.list.actions.new")}
                </Button>
                <Stack direction="row" spacing="10px" alignSelf={{ sm: "flex-start", lg: "auto" }}>
                    <PermitteesDocumentsFilter
                        filterSnapshot={searchParams.filters}
                        onFilter={(filters) => fetchData({ filters })}
                    />
                </Stack>
            </Flex>

            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                {response ? (
                    <>
                        {permitteeData && (
                            <PermitteesDocumentsListPageCardHeader permitteeData={permitteeData} />
                        )}
                        <ListPageCardHeader title={t("documents.title")} subtitle={t("documents.description")} />
                        <CardBody>
                            <ListFiltersActiveAlert show={hasActiveFilters} onClear={clearPermitteesDocumentsFilters} />
                            <SearchTable2
                                columnsData={columns}
                                tableData={response.content}
                                pageableData={toTablePageableData(response, {
                                    orderBy: searchParams.orderBy ?? "data_criacao",
                                    orderDirection: searchParams.orderDirection ?? "desc",
                                    ...searchParams
                                })}
                                onChange={fetchData}
                                disableSearch={true}
                            />
                            {(isLoadingDelete || isLoadingPermitteeData) && (
                                <Box
                                    position="fixed"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                    bg="rgba(255, 255, 255, 0.6)"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    zIndex="overlay"
                                >
                                    <Loading maxH={{ sm: "100px", md: "350px" }} />
                                </Box>
                            )}
                        </CardBody>
                    </>
                ) : (
                    <Loading maxH={{ sm: "100px", md: "350px" }} />
                )}
            </Card>

            <ConfirmDialog ref={dialogRef} onConfirm={handleConfirmDelete} confirmButtonProps={{ colorScheme: "red" }} />
        </Flex>
    );
}

export default PermitteesDocumentsList;
